/*
 * Cobuntu Messages Widget v1
 *
 * Drop-in chat icon + unread badge for white-label community landing
 * pages. Click navigates to the cobuntu messaging surface
 * (/conversations); polls the messaging service every 60s to keep the
 * unread badge fresh.
 *
 * Why not embed the full chat UI in the widget:
 *   The chat UI runs sockets, manages multiple conversation streams,
 *   handles message composition, file attachments, voice notes, etc.
 *   Bundling all of that into a third-party-site widget would balloon
 *   the bundle ~30-50× and create state-sync nightmares with the React
 *   app on cobuntu pages. The widget intentionally stays as a
 *   *navigation affordance* + unread indicator; the real UI lives at
 *   /conversations where the cobuntu app already runs.
 *
 * Auth gating: unread API requires auth. Logged-out users see the
 * button but the badge stays hidden + click bounces to /login?next=
 * /conversations.
 *
 * Three levels of customization (Level 1 CSS vars / Level 2 data-* /
 * Level 3 render-yourself slots) — see docs/features/cobuntu-messages-
 * widget.md.
 */

// ─── Constants ────────────────────────────────────────────────────

const COOKIE_NAME = "cobuntu_auth";
const MOUNT_SELECTOR = "[data-cobuntu-messages]";
const MOUNTED_ATTR = "data-cobuntu-messages-mounted";
const STYLE_ID = "cobuntu-messages-widget-styles";
const POLL_INTERVAL_MS = 60_000;
const EXP_SKEW_MS = 30_000;

import { authedFetch } from "../api/authedFetch";
import { getCachedSession, primeSession } from "../shared/session";
import { appNav } from "../shared/origin";



// ─── State ────────────────────────────────────────────────────────

let unreadCount = 0;
let pollTimer: ReturnType<typeof setInterval> | null = null;

// ─── Cookie / auth check ──────────────────────────────────────────

/**
 * Is this visitor signed in? (app platform S3.)
 *
 * Reads the session the SERVER resolved, rather than decoding the auth cookie
 * here. S5 makes that cookie httpOnly — `document.cookie` returns nothing —
 * and the old decode would silently conclude "guest" for every signed-in
 * member on three live customer sites, with nothing thrown to notice.
 *
 * Synchronous on purpose: `init()` awaits `primeSession()` before the first
 * render, so by the time any of this file's render paths ask, the answer is
 * cached. Keeping it sync is what made this a small change instead of an async
 * rewrite of the render flow.
 */
function isAuthenticated(): boolean {
  return getCachedSession()?.authenticated === true;
}

// ─── Unread fetch + poll ──────────────────────────────────────────

async function fetchUnread(): Promise<number> {
  if (!isAuthenticated()) return 0;
  try {
    // No authToken: this is a same-origin path, so the browser attaches the
    // cookie itself and the apex proxy forwards it server-side. That keeps
    // working once the cookie is httpOnly; passing a JS-read token would not.
    const res = await authedFetch(`/api/messaging/conversations/unread/counts`);
    if (!res.ok) return unreadCount; // keep stale value on transient failure
    const body = (await res.json()) as { total?: number };
    return typeof body.total === "number" ? body.total : 0;
  } catch {
    return unreadCount;
  }
}

async function refresh(): Promise<void> {
  const next = await fetchUnread();
  if (next === unreadCount) return;
  unreadCount = next;
  updateAllBadges();
}

function startPolling(): void {
  if (pollTimer) return;
  refresh();
  pollTimer = setInterval(() => {
    if (document.visibilityState === "visible") refresh();
  }, POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refresh();
  });
}

// ─── Slot pattern (Level 3) ───────────────────────────────────────

function hasCustomSlots(host: HTMLElement): boolean {
  return !!host.querySelector("[data-cobuntu-bind], [data-cobuntu-action]");
}

function applySlot(host: HTMLElement): void {
  host.querySelectorAll<HTMLElement>("[data-cobuntu-bind='unread-count']").forEach((el) => {
    el.textContent = unreadCount > 99 ? "99+" : String(unreadCount);
    el.style.display = unreadCount > 0 ? "" : "none";
  });
  host.querySelectorAll<HTMLElement>("[data-cobuntu-show='has-unread']").forEach((el) => {
    el.style.display = unreadCount > 0 ? "" : "none";
  });
  host.querySelectorAll<HTMLElement>("[data-cobuntu-show='no-unread']").forEach((el) => {
    el.style.display = unreadCount === 0 ? "" : "none";
  });
  host.querySelectorAll<HTMLElement>("[data-cobuntu-action='open-messages']").forEach((el) => {
    if (el.getAttribute("data-cobuntu-bound") === "1") return;
    el.setAttribute("data-cobuntu-bound", "1");
    el.addEventListener("click", (e) => {
      e.preventDefault();
      navigateToMessages();
    });
  });
}

// ─── Default render (Level 1 + 2) ─────────────────────────────────

function renderDefault(host: HTMLElement): void {
  const variant = host.getAttribute("data-variant") || "icon"; // icon | button
  const size = host.getAttribute("data-size") || "md";
  const showBadge = host.getAttribute("data-show-badge") !== "false";
  const ariaLabel = host.getAttribute("data-label") || "Messages";
  const buttonLabel = host.getAttribute("data-button-label") || "Messages";
  const requireAuth = host.getAttribute("data-require-auth") || "prompt"; // prompt | hide

  // Logged-out + hide → render nothing. Collapse the mount with
  // display:none so the empty <div> doesn't keep occupying a slot in a
  // flex/gap header — that leftover slot surfaces as a stray gap
  // between the visible widgets (reported on PBN, 2026-06-09).
  if (!isAuthenticated() && requireAuth === "hide") {
    host.style.display = "none";
    host.innerHTML = "";
    return;
  }

  // A re-render (e.g. after sign-in) must undo any previous hide.
  host.style.display = "";
  host.innerHTML = "";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.setAttribute("aria-label", ariaLabel);
  btn.setAttribute("title", ariaLabel);
  // Auth gating: when the viewer is anonymous, render the trigger in a
  // muted "anon" variant so it's visually obvious it's not actionable
  // in its usual sense. Click still works — it routes to /login — but
  // the dimmer styling + aria-disabled cue prevents the "I see a chat
  // affordance but tapping it doesn't take me to my messages"
  // confusion reported by Bela on 2026-05-31.
  const anon = !isAuthenticated();
  btn.className =
    `cobuntu-messages-trigger cobuntu-messages-trigger--${variant} cobuntu-messages-trigger--${size}` +
    (anon ? " cobuntu-messages-trigger--anon" : "");
  if (anon) btn.setAttribute("aria-disabled", "true");
  btn.innerHTML = `
    ${chatIconSvg()}
    ${variant === "button" ? `<span class="cobuntu-messages-trigger__label">${escapeHtml(buttonLabel)}</span>` : ""}
    ${showBadge && !anon ? `<span class="cobuntu-messages-trigger__badge" hidden>0</span>` : ""}
  `;
  btn.addEventListener("click", navigateToMessages);
  host.appendChild(btn);
  updateBadgeOn(host);
}

function renderHost(host: HTMLElement): void {
  host.setAttribute(MOUNTED_ATTR, "");
  if (hasCustomSlots(host)) applySlot(host);
  else renderDefault(host);
}

function updateBadgeOn(host: HTMLElement): void {
  const badge = host.querySelector<HTMLElement>(".cobuntu-messages-trigger__badge");
  if (badge) {
    badge.textContent = unreadCount > 99 ? "99+" : String(unreadCount);
    if (unreadCount === 0) badge.setAttribute("hidden", "");
    else badge.removeAttribute("hidden");
  }
  // Slot bindings (Level 3 hosts mixed alongside default ones).
  if (hasCustomSlots(host)) applySlot(host);
}

function updateAllBadges(): void {
  document.querySelectorAll<HTMLElement>(MOUNT_SELECTOR).forEach(updateBadgeOn);
}

// ─── Navigation ───────────────────────────────────────────────────

function navigateToMessages(): void {
  if (!isAuthenticated()) {
    location.href = appNav(`/login?next=${encodeURIComponent("/conversations")}`);
    return;
  }
  location.href = "/conversations";
}

// ─── Mount + DOM watch ────────────────────────────────────────────

function mountAll(): void {
  // Do not paint before the session is known (S3). renderHost marks each host
  // with MOUNTED_ATTR and mountAll skips anything already marked, so a render
  // done with an unknown session is not merely an early paint — it STICKS. A
  // signed-in member would keep the anon variant until a full reload.
  //
  // Deferring here rather than in init() covers the MutationObserver path too:
  // a host added to the DOM later enters through the same door. primeSession()
  // is idempotent and cached, so re-entering costs nothing.
  if (getCachedSession() === null) {
    void primeSession().then(mountAll);
    return;
  }
  document
    .querySelectorAll<HTMLElement>(`${MOUNT_SELECTOR}:not([${MOUNTED_ATTR}])`)
    .forEach(renderHost);
}

let observer: MutationObserver | null = null;
function watchDom(): void {
  if (observer) return;
  observer = new MutationObserver(mountAll);
  observer.observe(document.body, { childList: true, subtree: true });
}

// ─── Helpers ──────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default: return "&#39;";
    }
  });
}

function chatIconSvg(): string {
  // Paper-airplane (lucide "send"). Replaces the previous chat-bubble
  // glyph 2026-05-26 — messaging is a sent-message medium, and the
  // airplane reads as "compose / send" while the bubble was reading
  // as "ambient chat".
  return `<svg class="cobuntu-messages-trigger__icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <line x1="22" y1="2" x2="11" y2="13"></line>
    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
  </svg>`;
}

// ─── Styles ───────────────────────────────────────────────────────

const CSS = `
.cobuntu-messages-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: var(--cobuntu-bg, transparent);
  color: var(--cobuntu-color, currentColor);
  border: var(--cobuntu-border, 0);
  border-radius: var(--cobuntu-radius, 0);
  padding: var(--cobuntu-padding, 0);
  cursor: pointer;
  font: inherit;
  font-family: var(--cobuntu-font, inherit);
  font-size: var(--cobuntu-font-size, 13px);
  font-weight: var(--cobuntu-font-weight, 500);
  letter-spacing: var(--cobuntu-letter-spacing, normal);
  text-transform: var(--cobuntu-text-transform, none);
  transition: opacity 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.cobuntu-messages-trigger--icon {
  width: 36px;
  height: 36px;
  padding: 0;
}
.cobuntu-messages-trigger--button {
  padding: var(--cobuntu-padding, 8px 16px);
}
.cobuntu-messages-trigger--sm.cobuntu-messages-trigger--icon { width: 32px; height: 32px; }
.cobuntu-messages-trigger--lg.cobuntu-messages-trigger--icon { width: 40px; height: 40px; }

.cobuntu-messages-trigger:hover { color: var(--cobuntu-hover-color, var(--cobuntu-color, currentColor)); background: var(--cobuntu-hover-bg, color-mix(in srgb, currentColor 10%, transparent)); border-radius: 9999px; }

.cobuntu-messages-trigger__badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9999px;
  background: var(--cobuntu-badge-bg, #d4af37);
  color: var(--cobuntu-badge-text, #000);
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  pointer-events: none;
  box-sizing: border-box;
}
.cobuntu-messages-trigger__badge[hidden] { display: none; }

/* Anon variant — visually muted but still clickable (routes to login).
   Bela 2026-05-31: anon viewers were seeing the chat affordance and
   tapping it felt like nothing happened (it just bounced to /login).
   The dimmer treatment + aria-disabled signal "not for you yet"
   without the full-hide approach the data-require-auth="hide" mode
   takes. */
.cobuntu-messages-trigger--anon {
  opacity: var(--cobuntu-anon-opacity, 0.4);
}
.cobuntu-messages-trigger--anon:hover {
  opacity: var(--cobuntu-anon-hover-opacity, 0.55);
}
`;

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

// ─── Boot ─────────────────────────────────────────────────────────

function init(): void {
  injectStyles();
  mountAll();
  watchDom();
  startPolling();
  // Live count from the community-app's React unread context (same tab). The
  // `storage`-style cross-tab gap doesn't apply here — the app dispatches this
  // whenever its socket-driven count changes, so the badge updates instantly
  // instead of waiting for the 60s poll (which stays as the fallback).
  window.addEventListener("cobuntu:messages-unread", (e) => {
    const c = (e as CustomEvent<{ count?: number }>).detail?.count;
    if (typeof c === "number" && c !== unreadCount) {
      unreadCount = c;
      updateAllBadges();
    }
  });
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}

// ─── Public API ───────────────────────────────────────────────────

declare global {
  interface Window {
    CobuntuMessages?: {
      unread: () => number;
      refresh: () => Promise<void>;
      open: () => void;
    };
  }
}

if (typeof window !== "undefined") {
  window.CobuntuMessages = {
    unread: () => unreadCount,
    refresh,
    open: navigateToMessages,
  };
}

// ─── Test exports ─────────────────────────────────────────────────

export const __test = {
  isAuthenticated,
  escapeHtml,
  COOKIE_NAME,
  MOUNT_SELECTOR,
};
